/**
 * Where a wall section's edge SNAPS while it is dragged in Edit wall (3D).
 *
 * A contractor dragging a shower-tile section to the wall's end could not get
 * it to land there: the floor's region handles have always snapped, the wall's
 * never did, and the 2D wall editor that once did (`WallIsolationView`) has had
 * no caller since `f44735e9f`.
 *
 * EVERYTHING HERE IS IN THE FRAME THE SECTION IS DRAWN IN. A section's
 * `x / y / width / height` are percentages of the wall face MESH's own extent —
 * the tile's UVs are normalised to it (`textureGeometry`) and the drag handles
 * read the pointer against it (`WallRegionHandles`). So the frame is taken from
 * that mesh (`wallRegionFrame`), never rebuilt from `center` / `dimensions`:
 * on a raked face the two disagree by up to 1.2 m vertically, and "snapped to
 * the ceiling" has to mean the drawn top. With every target expressed in the
 * drawn frame, a snapped edge lands on the drawn corner by construction.
 *
 * Targets, as `{ pct, kind, edge, id }` lines — `vertical` lines are positions
 * ALONG the wall (they catch left / right edges), `horizontal` ones are heights
 * (they catch top / bottom edges):
 *   - `wall-end`, `floor`, `ceiling`: the drawn face's extent (0 and 100);
 *   - `wall-corner`: the face polygon's own corners, which on a raked or
 *     stepped face add the knee of the slope and its low top;
 *   - `region`: every edge of the OTHER sections on this wall;
 *   - `opening`: the hole a door / window / opening cuts, by the carver's own
 *     rules (`alongWallSpan` along the wall; `cutsDownToFloor` +
 *     `openingHoleSpanY` up it, so a door runs to the floor);
 *   - `fixture`: a shower's, tub's or vanity's ends along the wall and its top,
 *     for the fixtures the elevation rule (`objectsNearWall`) puts on this
 *     wall, outermost piece only (`innerFixtureIds` — a countertop, not the
 *     cabinet under it);
 *   - `fixture-overhang`: a shower's ends and top pushed out by
 *     `SHOWER_TILE_OVERHANG_IN`, where tile usually stops;
 *   - `corner-match`: the bottom and top of each section on a wall that meets
 *     this one at a corner and reaches that corner, as real heights — so a
 *     shower's back-wall and side-wall tile can be made the same height.
 *     Lowest priority: this wall's own lines win a tie.
 * Anything that lands off the wall is dropped — you cannot drag there anyway.
 *
 * The snap distance is a physical one: `WALL_REGION_SNAP_DISTANCE_IN`,
 * converted per axis with the drawn length and height (`thresholdPct`), so the
 * pull is the same along and up a wall of any size.
 */
import { alongWallSpan } from '../components/3d/geometry/wallFaceCarve';
import {
  cutsDownToFloor,
  openingHoleSpanY,
} from '../components/3d/geometry/coplanarOpenings';
import { objectsNearWall } from './elevationWallObjects';
import { belongsToWall, isDemoedOpening } from './parentWallHelpers';
import { openingTypeOf } from './openingTypeConversion';
import { getObjectType } from './objectTypeUtils';
import { innerFixtureIds } from './regionSnapHelpers';
import { matchCornerNeighbours } from './trimAdjacency';
import { SHOWER_TILE_MARGIN_IN } from '@shared-lib/geometry/showerWallTiling';

const INCH_M = 0.0254;

/** How close (inches) an edge must come to a target to snap onto it. */
// 3": 1.5" proved too faint to notice in use; 6" would swallow a shower's
// edge and its 2" overhang into one zone and stop anything being placed
// within 6" of a line.
export const WALL_REGION_SNAP_DISTANCE_IN = 3;

/**
 * How far past a shower's edge (inches) its tile runs — the shower tile
 * rule's own margin (`SHOWER_TILE_MARGIN_IN`), so the guide sits where the
 * tiler stops the tile.
 */
export const SHOWER_TILE_OVERHANG_IN = SHOWER_TILE_MARGIN_IN;

const SHOWER_TYPES = new Set(['shower']);
const TUB_TYPES = new Set(['bathtub', 'tub-support-box', 'tub-surface']);
const VANITY_TYPES = new Set(['vanity', 'countertop', 'lower-cabinet']);

// Two targets closer than this are ONE target — and a target this close
// outside the wall is the wall's own end. A shower built into the corner ends
// on the wall's end to a few microns (Float32 mesh positions against float64
// data); a millimetre is invisible, and an order finer than the region's
// alpha map can draw. Without it the shower's end, a hair inside the corner,
// out-pulls the corner itself and the edge stops 0.1 µm short of it.
const COINCIDENT_M = 0.001;

// When targets coincide, the one kept — the face's own lines first, so a
// fixture flush with the corner snaps to exactly 0 / 100.
const KIND_PRIORITY = [
  'wall-end',
  'floor',
  'ceiling',
  'wall-corner',
  'opening',
  'fixture',
  'fixture-overhang',
  'region',
  'corner-match',
];

const dot = (p, axis) =>
  (p.x ?? 0) * axis.x + (p.y ?? 0) * axis.y + (p.z ?? 0) * axis.z;

/**
 * The frame a wall's region percentages are drawn in: the extent of the face
 * mesh's vertices along the wall's `right` axis and its `up` axis. The same
 * reduction the tile's UVs and the drag handles make.
 *
 * @param {{count: number, getX, getY, getZ}} positions - the face mesh's
 *   position attribute (world space — wall meshes carry no transform)
 * @param {{x, y, z}} right - along the wall (`getWallCoordinateSystem`)
 * @param {{x, y, z}} up
 * @returns {{right, up, uMin, uMax, vMin, vMax, start, end}|null} null for
 *   a face with no length or no height. `start` / `end` are the drawn face's
 *   two ends in plan (`{x, z}` at `uMin` / `uMax`) — where it meets the walls
 *   at its corners.
 */
export function wallRegionFrame(positions, right, up) {
  if (!positions?.count || !right || !up) return null;
  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;
  let start = null;
  let end = null;
  for (let i = 0; i < positions.count; i++) {
    const p = {
      x: positions.getX(i),
      y: positions.getY(i),
      z: positions.getZ(i),
    };
    const u = dot(p, right);
    const v = dot(p, up);
    if (u < uMin) {
      uMin = u;
      start = { x: p.x, z: p.z };
    }
    if (u > uMax) {
      uMax = u;
      end = { x: p.x, z: p.z };
    }
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  }
  if (!(uMax - uMin > 0) || !(vMax - vMin > 0)) return null;
  return {
    right: { x: right.x, y: right.y, z: right.z },
    up: { x: up.x, y: up.y, z: up.z },
    uMin,
    uMax,
    vMin,
    vMax,
    start,
    end,
  };
}

/**
 * Snap targets for one wall, in that wall's region percentages.
 *
 * @param {Object} args
 * @param {Object|null} args.frame - from `wallRegionFrame`
 * @param {Object} args.wall - the room wall (`id`, `center`, `rotation`,
 *   `dimensions`, `polygonCorners`)
 * @param {Array<{id, region}>} [args.regions] - the wall's sections
 * @param {string|null} [args.editingRegionId] - the section being dragged
 * @param {Array} [args.openings] - the room's HYDRATED openings
 * @param {Array} [args.objects] - the room's objects
 * @param {Array<{wall, frame, regions}>} [args.neighbours] - the room's OTHER
 *   walls, each with its own drawn frame and its saved sections; the ones
 *   sharing a corner with this wall lend their section heights
 * @returns {{vertical: Array, horizontal: Array, thresholdPct: {x, y}}|null}
 */
export function buildWallRegionSnapLines({
  frame,
  wall,
  regions = [],
  editingRegionId = null,
  openings = [],
  objects = [],
  neighbours = [],
}) {
  if (!frame || !wall) return null;
  const length = frame.uMax - frame.uMin;
  const height = frame.vMax - frame.vMin;
  const alongPct = (u) => ((u - frame.uMin) / length) * 100;
  const upPct = (v) => ((v - frame.vMin) / height) * 100;

  const coincidentPct = {
    x: (COINCIDENT_M / length) * 100,
    y: (COINCIDENT_M / height) * 100,
  };
  const vertical = [];
  const horizontal = [];
  const add = (list, pct, kind, edge, id = null) => {
    if (!Number.isFinite(pct)) return;
    const tol = list === vertical ? coincidentPct.x : coincidentPct.y;
    if (pct < -tol || pct > 100 + tol) return;
    list.push({ pct: Math.min(100, Math.max(0, pct)), kind, edge, id });
  };

  addFaceLines(frame, wall, { vertical, horizontal, add, alongPct, upPct });
  addRegionLines(regions, editingRegionId, { vertical, horizontal, add });
  addOpeningLines(frame, wall, openings, {
    vertical,
    horizontal,
    add,
    alongPct,
    upPct,
  });
  addFixtureLines(frame, wall, objects, {
    vertical,
    horizontal,
    add,
    alongPct,
    upPct,
  });
  addCornerMatchLines(frame, wall, neighbours, { horizontal, add, upPct });

  const snapM = WALL_REGION_SNAP_DISTANCE_IN * INCH_M;
  return {
    vertical: mergeCoincident(vertical, coincidentPct.x),
    horizontal: mergeCoincident(horizontal, coincidentPct.y),
    thresholdPct: { x: (snapM / length) * 100, y: (snapM / height) * 100 },
  };
}

const priorityOf = (line) => {
  const i = KIND_PRIORITY.indexOf(line.kind);
  return i === -1 ? KIND_PRIORITY.length : i;
};

/**
 * Collapse targets within `tolPct` of each other into the highest-priority
 * one, so a target flush with another is never a hair's breadth closer to the
 * pointer than the line it stands on.
 */
function mergeCoincident(lines, tolPct) {
  const sorted = [...lines].sort((a, b) => a.pct - b.pct);
  const merged = [];
  let cluster = [];
  const flush = () => {
    if (!cluster.length) return;
    merged.push(
      cluster.reduce((best, l) => (priorityOf(l) < priorityOf(best) ? l : best))
    );
    cluster = [];
  };
  for (const line of sorted) {
    if (cluster.length && line.pct - cluster[0].pct > tolPct) flush();
    cluster.push(line);
  }
  flush();
  return merged;
}

/** The face itself: its ends, floor, ceiling, and its polygon's corners. */
function addFaceLines(
  frame,
  wall,
  { vertical, horizontal, add, alongPct, upPct }
) {
  add(vertical, 0, 'wall-end', 'start');
  add(vertical, 100, 'wall-end', 'end');
  add(horizontal, 0, 'floor', 'bottom');
  add(horizontal, 100, 'ceiling', 'top');

  // Only a polygon face has corners of its own between those four; the mesh
  // is built from exactly these points (`PolygonWalls`).
  const corners = wall.polygonCorners;
  if (!(corners?.length >= 3)) return;
  for (const c of corners) {
    if (!Number.isFinite(c?.x) || !Number.isFinite(c?.z)) continue;
    // A corner on the face's extent is the end / floor / ceiling line
    // already, and merges into it.
    add(vertical, alongPct(dot(c, frame.right)), 'wall-corner', 'corner');
    if (!Number.isFinite(c.y)) continue;
    add(horizontal, upPct(dot(c, frame.up)), 'wall-corner', 'corner');
  }
}

/** Every edge of the other sections on this wall. */
function addRegionLines(
  regions,
  editingRegionId,
  { vertical, horizontal, add }
) {
  for (const r of regions || []) {
    if (!r?.region || r.id === editingRegionId) continue;
    const { x, y, width, height } = r.region;
    add(vertical, x, 'region', 'left', r.id);
    add(vertical, x + width, 'region', 'right', r.id);
    add(horizontal, y, 'region', 'bottom', r.id);
    add(horizontal, y + height, 'region', 'top', r.id);
  }
}

/**
 * The hole each opening on this wall cuts, by the carver's own rules — the
 * same two calls `PolygonWalls` makes, without the styled-door frame buffer
 * (the opening's edge is the target, not its casing).
 */
function addOpeningLines(
  frame,
  wall,
  openings,
  { vertical, horizontal, add, alongPct, upPct }
) {
  for (const opening of openings || []) {
    if (!belongsToWall(opening, wall.id)) continue;
    const { center, dimensions } = opening;
    if (!center || !dimensions) continue;

    const span = alongWallSpan(
      { center, dimensions, rotation: opening.rotation ?? wall.rotation },
      frame.right,
      { x: 0, z: 0 }
    );
    if (span.max - span.min > 0) {
      add(vertical, alongPct(span.min), 'opening', 'start', opening.id);
      add(vertical, alongPct(span.max), 'opening', 'end', opening.id);
    }

    const openingHeight = dimensions.y;
    if (!(openingHeight > 0)) continue;
    const centerV = dot(center, frame.up);
    const { bottom, top } = openingHoleSpanY({
      cutsToFloor: cutsDownToFloor({
        type: openingTypeOf(opening),
        isDemoed: isDemoedOpening(opening),
        reachedByCoplanarBridge: false,
        sillAboveFaceFloor: centerV - openingHeight / 2 - frame.vMin,
        opening,
      }),
      faceMinY: frame.vMin,
      centerV,
      openingHeight,
    });
    add(horizontal, upPct(bottom), 'opening', 'bottom', opening.id);
    add(horizontal, upPct(top), 'opening', 'top', opening.id);
  }
}

/** Which fixture a snap line's object is — 'shower', 'tub', 'vanity' or null. */
export const fixtureKind = (obj) => {
  const type = getObjectType(obj) || '';
  if (SHOWER_TYPES.has(type)) return 'shower';
  if (TUB_TYPES.has(type)) return 'tub';
  if (VANITY_TYPES.has(type)) return 'vanity';
  return null;
};

/**
 * A shower's, tub's or vanity's ends along the wall and its top, for the
 * fixtures the elevation rule puts on this wall. A shower adds the same three
 * edges pushed out by `SHOWER_TILE_OVERHANG_IN`.
 */
function addFixtureLines(
  frame,
  wall,
  objects,
  { vertical, horizontal, add, alongPct, upPct }
) {
  if (!objects?.length) return;
  const inner = innerFixtureIds(objects, getObjectType);
  const overhang = SHOWER_TILE_OVERHANG_IN * INCH_M;
  for (const obj of objectsNearWall(wall, objects)) {
    const kind = fixtureKind(obj);
    if (!kind || inner.has(obj.id)) continue;
    const { center, dimensions } = obj;
    if (!center || !dimensions) continue;

    const span = alongWallSpan(obj, frame.right, { x: 0, z: 0 });
    if (span.max - span.min > 0) {
      add(vertical, alongPct(span.min), 'fixture', 'start', obj.id);
      add(vertical, alongPct(span.max), 'fixture', 'end', obj.id);
      if (kind === 'shower') {
        add(
          vertical,
          alongPct(span.min - overhang),
          'fixture-overhang',
          'start',
          obj.id
        );
        add(
          vertical,
          alongPct(span.max + overhang),
          'fixture-overhang',
          'end',
          obj.id
        );
      }
    }

    if (!(dimensions.y > 0)) continue;
    const top = dot(center, frame.up) + dimensions.y / 2;
    add(horizontal, upPct(top), 'fixture', 'top', obj.id);
    if (kind === 'shower') {
      add(horizontal, upPct(top + overhang), 'fixture-overhang', 'top', obj.id);
    }
  }
}

/**
 * Heights of the sections on the walls that meet this one at its corners, so
 * tile heights line up around a corner — a shower's back-wall tile and its
 * side-wall tile.
 *
 * "Meets at a corner" is the trim mitre's rule (`matchCornerNeighbours`), on
 * each wall's DRAWN ends. Only a section that reaches the shared corner (its
 * corner-side edge within the snap distance of it) lends its heights: that is
 * the tile turning the corner, not something further along that wall. A
 * height is carried as a real height — off the neighbour's own frame, onto
 * this one — so walls with different floors or heights still line up.
 */
function addCornerMatchLines(
  frame,
  wall,
  neighbours,
  { horizontal, add, upPct }
) {
  if (!neighbours?.length || !frame.start || !frame.end) return;
  const byId = new Map();
  const cornersById = new Map();
  for (const n of neighbours) {
    const id = n?.wall?.id;
    if (!id || id === wall.id || !n.frame?.start || !n.frame?.end) continue;
    byId.set(id, n);
    cornersById.set(id, [n.frame.start, n.frame.end]);
  }
  const { prev, next } = matchCornerNeighbours(
    [frame.start, frame.end],
    wall.id,
    cornersById
  );
  for (const match of [prev, next]) {
    if (!match) continue;
    const n = byId.get(match.id);
    const nLength = n.frame.uMax - n.frame.uMin;
    const nHeight = n.frame.vMax - n.frame.vMin;
    const reachPct = ((WALL_REGION_SNAP_DISTANCE_IN * INCH_M) / nLength) * 100;
    for (const r of n.regions || []) {
      if (!r?.region) continue;
      const { x, y, width, height } = r.region;
      const reaches =
        match.shared === 'start' ? x <= reachPct : x + width >= 100 - reachPct;
      if (!reaches) continue;
      for (const [pct, edge] of [
        [y, 'bottom'],
        [y + height, 'top'],
      ]) {
        const realV = n.frame.vMin + (pct / 100) * nHeight;
        add(horizontal, upPct(realV), 'corner-match', edge, r.id);
      }
    }
  }
}
